import React, { FC } from 'react';

import {
  Avatar,
  AvatarFallback,
  AvatarGroup,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@column/ui';

const MAX_AVATARS = 3;

// A 3px gap where each avatar is overlapped by the next. It's cut out with a mask rather than painted as a ring so
// it stays true on hovered rows and in dark mode. The next 32px avatar starts 6px early, so its center sits at 42px.
const overlapMask =
  '[&:not(:last-child)]:[mask-image:radial-gradient(circle_at_42px_50%,transparent_18.5px,black_19px)]';

export interface RoleUser {
  id: string;
  name: string;
  initials: string;
}

interface RoleUsersProps {
  count: number;
  users: RoleUser[];
}

export const RoleUsers: FC<RoleUsersProps> = ({ count, users }) => {
  if (count === 0) {
    return <span className="text-sm text-muted-foreground">No users</span>;
  }

  const visibleUsers = users.slice(0, MAX_AVATARS);

  return (
    <div className="flex items-center gap-2">
      {visibleUsers.length > 0 && (
        <TooltipProvider>
          <AvatarGroup className="-space-x-1.5 *:data-[slot=avatar]:ring-0">
            {visibleUsers.map((user) => (
              // The trigger sits inside the avatar so the avatar keeps its own data-slot.
              <Avatar key={user.id} className={overlapMask}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AvatarFallback className="bg-gray-100 text-[11px] font-medium text-gray-600 uppercase shadow-[inset_0_0_0_1px_rgb(0_0_0/0.08)] dark:bg-gray-800 dark:text-gray-300 dark:shadow-[inset_0_0_0_1px_rgb(255_255_255/0.1)]">
                      {user.initials}
                    </AvatarFallback>
                  </TooltipTrigger>
                  <TooltipContent side="top">{user.name}</TooltipContent>
                </Tooltip>
              </Avatar>
            ))}
          </AvatarGroup>
        </TooltipProvider>
      )}
      <span className="text-sm font-medium text-gray-600 tabular-nums dark:text-gray-300">
        {count === 1 ? '1 User' : `${count} Users`}
      </span>
    </div>
  );
};
